import {Component, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {Input} from '@angular/core';
import {
  AppsGroups,
  FLOW_RESOURCE_TYPES,
  FlowDescriptionEndpoint,
  FlowDescriptionNetworkRule,
  FlowDescriptionSide,
  ResourceFlowDescriptionRule
} from "@app/netscope/model/data-models";
import {
  NetscopeApiService,
  Success,
  Response
} from "@app/services/netscope-api.service";
import {of} from "rxjs";
import {
  AppsGroupsComboboxComponent
} from "@app/netscope/netscope-common-flowview/apps-groups-combobox/apps-groups-combobox.component";
import {
  AppsGroupsResolver,
  describeEndpoint,
  FlowDescriptionEndpointDescription
} from "@app/netscope/model/apps-groups-resolver";

/**
 * Edition of a network rule. Each side of the rule, its source and its destination, is either any end, or a
 * list of ends mixing resources, resource types, apps groups and networks: a flow matches the side as soon as
 * one of them does.
 */
@Component({
  standalone: false,
  selector: 'app-flows-descriptions-network-rule-edit',
  templateUrl: './flows-descriptions-network-rule-edit.component.html',
  styleUrl: './flows-descriptions-network-rule-edit.component.css'
})
export class FlowsDescriptionsNetworkRuleEditComponent implements OnInit, OnChanges {

  // Network rules are edited one at a time in a modal, but the radio groups names have to stay unique in case
  // several instances of this component live in the same page
  static instancesCounter = 0;
  radioGroupsSuffix = `${FlowsDescriptionsNetworkRuleEditComponent.instancesCounter++}`;

  sides: FlowDescriptionSide[] = ["source", "destination"];

  resourceTypes = FLOW_RESOURCE_TYPES;

  @Input()
  networkRule: FlowDescriptionNetworkRule;

  // Apps groups the rule can point to, shared with the page so that the files are loaded once
  @Input()
  appsGroupsResolver: AppsGroupsResolver;

  // Name of a resource of the flows, to list the resources an apps group stands for
  @Input()
  resourceName: (uuid: string) => string = () => undefined;

  vms = [];

  // Resources of each side, as the combobox edits them: they are written back into the ends of the rule
  resourcesBySide: {[side: string]: ResourceFlowDescriptionRule[]} = {source: [], destination: []};

  // Apps group being picked on each side, before it is added
  selectedFileBySide: {[side: string]: string} = {source: undefined, destination: undefined};
  selectedAppGroupBySide: {[side: string]: string} = {source: undefined, destination: undefined};

  // Network being typed on each side, before it is added
  networkBySide: {[side: string]: string} = {source: "", destination: ""};

  constructor(private netscopeService: NetscopeApiService) {
  }

  ngOnInit() {
    this.netscopeService.getLastInfrastructureVersion().subscribe((infrastructureVersionResponse: Response<any>) => {
      let infrastructureVersion;

      if (infrastructureVersionResponse instanceof Success) {
        infrastructureVersion = infrastructureVersionResponse.element;
      }

      this.vms = infrastructureVersion.topology.vm_only_topology.vms;
    });

    this.appsGroupsResolver?.loadFilesList();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["networkRule"] != undefined && this.networkRule != undefined) {
      for (let side of this.sides) {
        this.resourcesBySide[side] = this.endpointsOfKind(side, "resource")
          .map((endpoint) => ({uuid: endpoint.uuid, name: endpoint.name}));
      }
    }
  }

  fetchVms = (filter = '', combobox: AppsGroupsComboboxComponent) => {
    combobox.loadingResources = true;

    let resources = this.vms
      .filter((o) => o !== undefined)
      // @ts-ignore
      .filter((o) => o.uuid.indexOf(filter) !== 1 || o.name.indexOf(filter) !== -1);

    combobox.asyncResources$ = of(resources);
    combobox.loadingResources = false;
  }

  endpointsOfKind = (side: FlowDescriptionSide, kind: string): FlowDescriptionEndpoint[] => {
    return this.networkRule.endpointsOf(side).filter((endpoint) => endpoint.kind == kind);
  }

  setMode = (side: FlowDescriptionSide, mode: "any" | "specific") => {
    if (side == "source") {
      this.networkRule.sourceMode = mode;
    } else {
      this.networkRule.destinationMode = mode;
    }
  }

  // The combobox holds the resources of the side: the other ends are kept as they are, after them
  resourcesChanged = (side: FlowDescriptionSide, resources: ResourceFlowDescriptionRule[]) => {
    this.resourcesBySide[side] = resources ?? [];

    let resourceEndpoints = this.resourcesBySide[side]
      .map((resource) => FlowDescriptionEndpoint.resource(resource.uuid, resource.name));
    let otherEndpoints = this.networkRule.endpointsOf(side).filter((endpoint) => endpoint.kind != "resource");

    this.networkRule.setEndpoints(side, resourceEndpoints.concat(otherEndpoints));
  }

  removeEndpoint = (side: FlowDescriptionSide, endpoint: FlowDescriptionEndpoint) => {
    this.networkRule.setEndpoints(side, this.networkRule.endpointsOf(side).filter((e) => e != endpoint));
  }

  //////////////////////////////
  // Resource types
  //////////////////////////////

  isResourceTypeSelected = (side: FlowDescriptionSide, resourceType: string): boolean => {
    return this.endpointsOfKind(side, "resource_type").some((endpoint) => endpoint.resourceType == resourceType);
  }

  setResourceTypeSelected = (side: FlowDescriptionSide, resourceType: string, selected: boolean) => {
    let others = this.networkRule.endpointsOf(side)
      .filter((endpoint) => !(endpoint.kind == "resource_type" && endpoint.resourceType == resourceType));

    this.networkRule.setEndpoints(side, selected ? others.concat([FlowDescriptionEndpoint.resourceType(resourceType)]) : others);
  }

  //////////////////////////////
  // Apps groups
  //////////////////////////////

  fileSelected = async (side: FlowDescriptionSide, file: string) => {
    this.selectedFileBySide[side] = file;
    this.selectedAppGroupBySide[side] = undefined;
    await this.appsGroupsResolver.loadFiles([file]);
  }

  // Apps groups of the file picked on a side, the ones the side already lists being left out
  appsGroupsOfSelectedFile = (side: FlowDescriptionSide): AppsGroups[] => {
    let file = this.selectedFileBySide[side];
    let alreadyListed = this.endpointsOfKind(side, "apps_group")
      .filter((endpoint) => endpoint.file == file)
      .map((endpoint) => endpoint.name);

    return (this.appsGroupsResolver?.appsGroupsOf(file) ?? [])
      .filter((appGroup) => !alreadyListed.includes(appGroup.name));
  }

  addAppGroup = (side: FlowDescriptionSide) => {
    let file = this.selectedFileBySide[side];
    let name = this.selectedAppGroupBySide[side];

    if (file == undefined || name == undefined) {
      return;
    }

    this.networkRule.setEndpoints(side, this.networkRule.endpointsOf(side).concat([FlowDescriptionEndpoint.appsGroup(name, file)]));
    this.selectedAppGroupBySide[side] = undefined;
  }

  describeEndpoint = (endpoint: FlowDescriptionEndpoint): FlowDescriptionEndpointDescription => {
    return describeEndpoint(endpoint, this.appsGroupsResolver, this.resourceName);
  }

  //////////////////////////////
  // Networks
  //////////////////////////////

  addNetwork = (side: FlowDescriptionSide) => {
    let address = (this.networkBySide[side] ?? "").trim();

    if (address == "" || this.endpointsOfKind(side, "network").some((endpoint) => endpoint.address == address)) {
      return;
    }

    this.networkRule.setEndpoints(side, this.networkRule.endpointsOf(side).concat([FlowDescriptionEndpoint.network(address)]));
    this.networkBySide[side] = "";
  }
}
